import { createBrowserRouter, RouterProvider, redirect } from "react-router-dom";

import routeNames from "constants/route-names";
import PrivateRoute from "components/private-route/private-route";
import LoggedNavigationLayout from "components/layouts/logged-navigation-layout/logged-navigation-layout";
import LayoutManager from "components/layouts/layout-manager/layout-manager";
import { AuthLayout } from "components/layouts/auth-layout/auth-layout";

// import VerifyCode from "./verify-code/verify-code";
// import NewPassword from "./new-password/new-password";
import LogIn from "./login/login";
import SignUpScreen from "./sign-up/sign-up";
import ForgotPassword from "./forgot-password/forgot-password";
import EmailVerification from "./email-verification/email-verification";
import ErrorPage from "./error/error-page";
import Dashboard from "./dashboard/dashboard";
import InstalledProducts from "./installed-products/installed-products";
import Notifications from "./notifications/notifications";
import Order from "./order/order";
import RequestService from "./request-service/request-service";
import AiChat from "./ai-chat/ai-chat";
import InstalledDeviceDetails from "./installed-device-details/installed-device-details";

import AdminDashboard from "./admin-dashboard/admin-dashboard";
import AdminTiers from "./admin-tiers/admin-tiers";
import AdminAiModels from "./admin-ai-models/admin-ai-models";
import Intelligence from "./intelligence/intelligence";
import EagleEye from "./eagle-eye/eagle-eye";
import EagleEyeVideoSearch from "./eagle-eye-video-search/eagle-eye-video-search";

import AdminProducts from "./admin-products/admin-products";
import AdminProductDetails from "./admin-product-details/admin-product-details";
import ProductAdd from "./product-add/product-add";
import ProductEdit from "./product-edit/product-edit";

import AdminOrganizations from "./admin-organizations/admin-organizations";
import OrganizationAdd from "./organization-add/organization-add";
import OrganizationDetails from "./organization-details/organization-details";
import OrganizationEdit from "./organization-edit/organization-edit";

import AdminUsers from "./admin-users/admin-users";
import UserAdd from "./user-add/user-add";
import UserEdit from "./user-edit/user-edit";
import UserDetail from "./user-detail/user-detail";

import AdminProperties from "./admin-properties/admin-properties";
import PropertyDetails from "./property-details/property-details";
import PropertyAdd from "./property-add/property-add";
import PropertyEdit from "./property-edit/property-edit";
import InstalledProductsEdit from "./installed-products-edit/installed-products-edit";
import InstalledProductsAdd from "./installed-products-add/installed-products-add";
import ShopProductsAdd from "./shop-product-add/shop-product-add";
import ShopProductDetails from "./shop-product-details/shop-product-details";
import Checkout from "./checkout/checkout";

import AdminDeviceDetections from "./admin-device-detections/admin-device-detections";
import DeviceDetectionJob from "./device-detection/device-detection";

import Guides from "./guides/guides";
import GuideAdd from "./guide-add/guide-add";
import GuideDetails from "./guide-details/guide-details";

import NotFound from "./not-found/not-found";
import Handovers from "./handovers/handovers";
import HandoverAdd from "./handover-add/handover-add";
import ProofCenter from "./proof-center/proof-center";
import SystemDetails from "./system-details/system-details";
import HandoverDetails from "./handover-details/handover-details";
import HandoverDeviceDetection from "./handover-device-detection/handover-device-detection";

import OllieChatPage from "./ollie-chat/ollie-chat-page";
import InspectorDashboard from "./inspector-dashboard/inspector-dashboard";
import OllieInspectionAdd from "./ollie-inspection-add/ollie-inspection-add";
import OllieEvidenceCollection from "./ollie-evidence-collection/ollie-evidence-collection";
import InspectionDetails from "./ollie-inspection-details/inspection-details";
import PlanReviewAdd from "./plan-review-add/plan-review-add";
import PlanReviewCollection from "./plan-review-collection/plan-review-collection";
import PlanReviewDetails from "./plan-review-details/plan-review-details";
import PlanReviewSubmissions from "./plan-review-submissions/plan-review-submissions";
import PlanReviewComments from "./plan-review-comments/plan-review-comments";
import PlanReviewRules from "./plan-review-rules/plan-review-rules";
import PlanReviewCodes from "./plan-review-codes/plan-review-codes";
import PlanReviewCodeImports from "./plan-review-code-imports/plan-review-code-imports";
import PlanReviewCodeImport from "./plan-review-code-import/plan-review-code-import";
import PlanReviewRulesExtraction from "./plan-review-rules-extraction/plan-review-rules-extraction";
import PlanReviewExtractions from "./plan-review-extractions/plan-review-extractions";
import PlanReviewResources from "./plan-review-resources/plan-review-resources";
import PlanReviewSubmissionTypes from "./plan-review-submission-types/plan-review-submission-types";
import PlanReviewFeeSchedule from "./plan-review-fee-schedule/plan-review-fee-schedule";
import PlanReviewCategories from "./plan-review-categories/plan-review-categories";
import BoundaryAuthorityProposals from "./boundary-authority-proposals/boundary-authority-proposals";
import BoundaryLayers from "./boundary-layers/boundary-layers";
import ClaimJurisdiction from "./claim-jurisdiction/claim-jurisdiction";
import ClaimJurisdictionSetup from "./claim-jurisdiction-setup/claim-jurisdiction-setup";
import AdminAuthorityClaims from "./admin-authority-claims/admin-authority-claims";

const isProdEnv = process.env.NODE_ENV === "production";
const featureFlagGuideEnabled = process.env.REACT_APP_FF_GUIDE_ENABLED == "true" || true;
const featureFlagEagleEyeEnabled = process.env.REACT_APP_FF_EAGLE_EYE_ENABLED == "true" || false;
const featureFlagIntelligenceEnabled = process.env.REACT_APP_FF_INTELLIGENCE_ENABLED == "true" || false;
const featureFlagHandoversEnabled = process.env.REACT_APP_FF_HANDOVERS_ENABLED == "true" || false;
const featureFlagProofCenterEnabled = process.env.REACT_APP_FF_PROOF_CENTER_ENABLED == "true" || true;
const featureFlagOllieEnabled = process.env.REACT_APP_FF_IRIS_ENABLED == "true" || false;
const featureFlagOllieChatEnabled = process.env.REACT_APP_FF_OLLIE_CHAT_ENABLED == "true";

const router = createBrowserRouter([
	{
		element: <AuthLayout />,
		children: [
			{
				path: routeNames.HOME,
				element: <LogIn />,
				errorElement: <ErrorPage />
			},
			{
				path: routeNames.SIGNUP,
				element: <SignUpScreen />,
				errorElement: <ErrorPage />
			},
			{
				path: routeNames.LOGIN,
				element: <LogIn />,
				errorElement: <ErrorPage />
			},
			{
				path: routeNames.FORGOT_PASSWORD,
				element: <ForgotPassword />,
				errorElement: <ErrorPage />
			},
			{
				path: routeNames.EMAIL_VERIFICATION_PENDING,
				element: <EmailVerification />,
				errorElement: <ErrorPage />
			},
			{
				path: routeNames.CLAIM_JURISDICTION,
				element: <ClaimJurisdiction />,
				errorElement: <ErrorPage />
			},
			{
				path: routeNames.CLAIM_JURISDICTION_SETUP,
				element: <ClaimJurisdictionSetup />,
				errorElement: <ErrorPage />
			},
			...(featureFlagProofCenterEnabled
				? [
						{
							path: routeNames.PROOF_CENTER,
							element: <ProofCenter />,
							errorElement: <ErrorPage />
						},
						{
							path: routeNames.PROOF_CENTER_SYSTEM_DETAILS,
							element: <SystemDetails />,
							errorElement: <ErrorPage />
						}
					]
				: []),
			// {
			//   path: routeNames.VERIFY_CODE,
			//   element: <VerifyCode />,
			//   errorElement: <ErrorPage />,
			// },
			// {
			//   path: routeNames.NEW_PASSWORD,
			//   element: <NewPassword />,
			//   errorElement: <ErrorPage />,
			// },
			{
				element: (
					<PrivateRoute noPermissionsRequired={true}>
						<LoggedNavigationLayout />
					</PrivateRoute>
				),
				children: [
					{
						path: routeNames.DASHBOARD,
						element: <Dashboard />,
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.INSTALLED_DEVICES,
						element: <InstalledProducts />,
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.INSTALLED_PRODUCTS,
						loader: () => redirect(routeNames.INSTALLED_DEVICES)
					},
					{
						path: routeNames.INSTALLED_DEVICE_DETAILS,
						element: <InstalledDeviceDetails />,
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.INSTALLED_DEVICES_DETAILS,
						element: <InstalledDeviceDetails />,
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.INSTALLED_PRODUCTS_DETAILS,
						loader: ({ params }) =>
							redirect(routeNames.INSTALLED_DEVICES_DETAILS.replace(":installedDevId", params.installedDevId!))
					},
					{
						path: routeNames.NOTIFICATIONS,
						element: <Notifications />,
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ORDER,
						element: <Order />,
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.SHOP_PRODUCTS_DETAILS,
						element: <ShopProductDetails />,
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.CHECKOUT,
						element: <Checkout />,
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.REQUEST_SERVICE,
						element: <RequestService />,
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.AI_CHAT,
						element: <AiChat />,
						errorElement: <ErrorPage />
					},
					...(featureFlagGuideEnabled
						? [
								{
									path: routeNames.GUIDES,
									element: <Guides />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.GUIDE_DETAILS_EDIT,
									element: <GuideDetails isUserEditing />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.GUIDE_ADD,
									element: <GuideAdd />,
									errorElement: <ErrorPage />
								}
							]
						: []),
					...(featureFlagEagleEyeEnabled
						? [
								{
									path: routeNames.EAGLE_EYE,
									element: <EagleEyeVideoSearch />,
									errorElement: <ErrorPage />
								},
								...(!isProdEnv
									? [
											{
												path: routeNames.EE_API_TESTER,
												element: <EagleEye />,
												errorElement: <ErrorPage />
											}
										]
									: [])
							]
						: []),
					...(featureFlagHandoversEnabled
						? [
								{
									path: routeNames.HANDOVERS,
									element: <Handovers />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.HANDOVERS_ADD,
									element: <HandoverAdd />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.HANDOVER_DETAILS_EDIT,
									element: <HandoverAdd />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.HANDOVER_DEVICE_DETECTION_ADD,
									element: <HandoverDeviceDetection />,
									errorElement: <ErrorPage />
								},
								{
									// Device detection launched from a handover — keeps LoggedNavigationLayout
									// with Handovers sidebar item active (isHandoverContext = true).
									path: routeNames.HANDOVER_DEVICE_DETECTION_DETAILS,
									element: <HandoverDeviceDetection />,
									errorElement: <ErrorPage />
								}
							]
						: []),
					{
						// Deprecated /iris/* URLs, forwarded to their /ollie equivalent with the
						// rest of the path, query and hash intact. Left outside the feature flag
						// so an old link never behaves differently from the current one, and kept
						// loader-only so the rewrite happens before any layout or auth guard runs
						// — a logged-out visitor lands on login with the /ollie URL remembered.
						path: routeNames.LEGACY_IRIS_SUBTREE,
						loader: ({ request }) => {
							const url = new URL(request.url);
							const rest = url.pathname.slice(routeNames.LEGACY_IRIS_PREFIX.length);
							return redirect(`${routeNames.OLLIE}${rest}${url.search}${url.hash}`);
						}
					},
					...(featureFlagOllieEnabled
						? [
								{
									// The old /ollie overview screen was removed. Old links and bookmarks to
									// /ollie are sent to the dashboard. The route only has a loader, so the
									// redirect runs before any layout or auth guard.
									path: routeNames.OLLIE,
									loader: () => redirect(routeNames.OLLIE_DASHBOARD)
								},
								...(featureFlagOllieChatEnabled
									? [
											{
												path: routeNames.OLLIE_CHAT,
												element: <OllieChatPage />,
												errorElement: <ErrorPage />
											}
										]
									: []),
								{
									path: routeNames.OLLIE_DASHBOARD,
									element: <InspectorDashboard />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.OLLIE_INSPECTION_ADD,
									element: <OllieInspectionAdd />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.OLLIE_EVIDENCE_COLLECTION,
									element: <OllieEvidenceCollection />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.OLLIE_INSPECTION_DETAILS,
									element: <InspectionDetails />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_ADD,
									element: <PlanReviewAdd />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_COLLECTION,
									element: <PlanReviewCollection />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_COLLECTION_RESUBMIT,
									element: <PlanReviewCollection />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_RULES,
									element: (
										<PrivateRoute requiredRoute={routeNames.PLAN_REVIEW_RULES} redirectPath={routeNames.DASHBOARD}>
											<PlanReviewRules />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_EXTRACTIONS,
									element: (
										<PrivateRoute
											requiredRoute={routeNames.PLAN_REVIEW_EXTRACTIONS}
											redirectPath={routeNames.DASHBOARD}
										>
											<PlanReviewExtractions />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_RULES_EXTRACTION,
									element: (
										<PrivateRoute
											requiredRoute={routeNames.PLAN_REVIEW_RULES_EXTRACTION}
											redirectPath={routeNames.DASHBOARD}
										>
											<PlanReviewRulesExtraction />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_RESOURCES,
									element: (
										<PrivateRoute requiredRoute={routeNames.PLAN_REVIEW_RESOURCES} redirectPath={routeNames.DASHBOARD}>
											<PlanReviewResources />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_SUBMISSION_TYPES,
									element: (
										<PrivateRoute
											requiredRoute={routeNames.PLAN_REVIEW_SUBMISSION_TYPES}
											redirectPath={routeNames.DASHBOARD}
										>
											<PlanReviewSubmissionTypes />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_FEE_SCHEDULE,
									element: (
										<PrivateRoute requiredRoute={routeNames.PLAN_REVIEW_FEE_SCHEDULE} redirectPath={routeNames.DASHBOARD}>
											<PlanReviewFeeSchedule />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_CATEGORIES,
									element: (
										<PrivateRoute requiredRoute={routeNames.PLAN_REVIEW_CATEGORIES} redirectPath={routeNames.DASHBOARD}>
											<PlanReviewCategories />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_CODES,
									element: (
										<PrivateRoute requiredRoute={routeNames.PLAN_REVIEW_CODES} redirectPath={routeNames.DASHBOARD}>
											<PlanReviewCodes />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_CODE_IMPORTS,
									element: (
										<PrivateRoute requiredRoute={routeNames.PLAN_REVIEW_CODE_IMPORTS} redirectPath={routeNames.DASHBOARD}>
											<PlanReviewCodeImports />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_CODE_IMPORT,
									element: (
										<PrivateRoute requiredRoute={routeNames.PLAN_REVIEW_CODE_IMPORT} redirectPath={routeNames.DASHBOARD}>
											<PlanReviewCodeImport />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.BOUNDARY_AUTHORITY_PROPOSALS,
									element: (
										<PrivateRoute
											requiredRoute={routeNames.BOUNDARY_AUTHORITY_PROPOSALS}
											redirectPath={routeNames.DASHBOARD}
										>
											<BoundaryAuthorityProposals />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.BOUNDARY_LAYERS,
									element: (
										<PrivateRoute requiredRoute={routeNames.BOUNDARY_LAYERS} redirectPath={routeNames.DASHBOARD}>
											<BoundaryLayers />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.ADMIN_AUTHORITY_CLAIMS,
									element: (
										<PrivateRoute requiredRoute={routeNames.ADMIN_AUTHORITY_CLAIMS} redirectPath={routeNames.DASHBOARD}>
											<AdminAuthorityClaims />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_DETAILS,
									element: <PlanReviewDetails />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_SUBMISSIONS,
									element: <PlanReviewSubmissions />,
									errorElement: <ErrorPage />
								},
								{
									path: routeNames.PLAN_REVIEW_COMMENTS,
									element: <PlanReviewComments />,
									errorElement: <ErrorPage />
								}
							]
						: []),
					// Admin routes
					{
						path: routeNames.ADMIN_DASHBOARD,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_DASHBOARD} redirectPath={routeNames.DASHBOARD}>
								<AdminDashboard />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_DEVICES,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_DEVICES} redirectPath={routeNames.DASHBOARD}>
								<AdminProducts />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_PRODUCTS,
						loader: () => redirect(routeNames.ADMIN_DEVICES)
					},
					{
						path: routeNames.ADMIN_DEVICES_ADD,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_DEVICES_ADD} redirectPath={routeNames.DASHBOARD}>
								<ProductAdd />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_PRODUCTS_ADD,
						loader: () => redirect(routeNames.ADMIN_DEVICES_ADD)
					},
					{
						path: routeNames.ADMIN_DEVICES_DETAILS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_DEVICES_DETAILS} redirectPath={routeNames.DASHBOARD}>
								<AdminProductDetails />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_PRODUCTS_DETAILS,
						loader: ({ params }) => redirect(routeNames.ADMIN_DEVICES_DETAILS.replace(":deviceId", params.productId!))
					},
					{
						path: routeNames.ADMIN_DEVICES_EDIT,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_DEVICES_EDIT} redirectPath={routeNames.DASHBOARD}>
								<ProductEdit />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_PRODUCTS_EDIT,
						loader: ({ params }) => redirect(routeNames.ADMIN_DEVICES_EDIT.replace(":deviceId", params.productId!))
					},
					{
						path: routeNames.ADMIN_USERS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_USERS} redirectPath={routeNames.DASHBOARD}>
								<AdminUsers />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_USER_ADD,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_USER_ADD} redirectPath={routeNames.DASHBOARD}>
								<UserAdd />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_USER_DETAILS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_USER_DETAILS} redirectPath={routeNames.DASHBOARD}>
								<UserDetail />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_USER_EDIT,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_USER_EDIT} redirectPath={routeNames.DASHBOARD}>
								<UserEdit />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_ORGANIZATIONS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_ORGANIZATIONS} redirectPath={routeNames.DASHBOARD}>
								<AdminOrganizations />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_ORGANIZATION_ADD,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_ORGANIZATION_ADD} redirectPath={routeNames.DASHBOARD}>
								<OrganizationAdd />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_ORGANIZATION_DETAILS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_ORGANIZATION_DETAILS} redirectPath={routeNames.DASHBOARD}>
								<OrganizationDetails />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_ORGANIZATION_EDIT,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_ORGANIZATION_EDIT} redirectPath={routeNames.DASHBOARD}>
								<OrganizationEdit />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_PROPERTIES,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_PROPERTIES} redirectPath={routeNames.DASHBOARD}>
								<AdminProperties />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_PROPERTY_ADD,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_PROPERTY_ADD} redirectPath={routeNames.DASHBOARD}>
								<PropertyAdd />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_PROPERTY_EDIT,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_PROPERTY_EDIT} redirectPath={routeNames.DASHBOARD}>
								<PropertyEdit />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_PROPERTY_DETAILS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_PROPERTY_DETAILS} redirectPath={routeNames.DASHBOARD}>
								<PropertyDetails />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_INSTALLED_DEVICE_ADD,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_INSTALLED_DEVICE_ADD} redirectPath={routeNames.DASHBOARD}>
								<InstalledProductsAdd />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_INSTALLED_PRODUCT_ADD,
						loader: () => redirect(routeNames.ADMIN_INSTALLED_DEVICE_ADD)
					},
					{
						path: routeNames.ADMIN_INSTALLED_DEVICE_EDIT,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_INSTALLED_DEVICE_EDIT} redirectPath={routeNames.DASHBOARD}>
								<InstalledProductsEdit />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_INSTALLED_PRODUCT_EDIT,
						loader: ({ params }) =>
							redirect(routeNames.ADMIN_INSTALLED_DEVICE_EDIT.replace("/:installedDevId", params.installedProdId!))
					},
					{
						path: routeNames.ADMIN_INSTALLED_DEVICES_DETAILS,
						element: (
							<PrivateRoute
								requiredRoute={routeNames.ADMIN_INSTALLED_DEVICES_DETAILS}
								redirectPath={routeNames.DASHBOARD}
							>
								<InstalledDeviceDetails />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_INSTALLED_PRODUCTS_DETAILS,
						loader: ({ params }) =>
							redirect(routeNames.ADMIN_INSTALLED_DEVICES_DETAILS.replace(":installedDevId", params.installedDevId!))
					},
					{
						path: routeNames.ADMIN_TIERS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_TIERS} redirectPath={routeNames.DASHBOARD}>
								<AdminTiers />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_AI_MODELS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_AI_MODELS} redirectPath={routeNames.DASHBOARD}>
								<AdminAiModels />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_SHOP_PRODUCT_ADD,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_SHOP_PRODUCT_ADD} redirectPath={routeNames.DASHBOARD}>
								<ShopProductsAdd />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_SHOP_PRODUCTS_DETAILS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_SHOP_PRODUCTS_DETAILS} redirectPath={routeNames.DASHBOARD}>
								<ShopProductDetails />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					...(featureFlagIntelligenceEnabled
						? [
								{
									path: routeNames.ADMIN_INTELLIGENCE,
									element: (
										<PrivateRoute requiredRoute={routeNames.ADMIN_INTELLIGENCE} redirectPath={routeNames.DASHBOARD}>
											<Intelligence />
										</PrivateRoute>
									),
									errorElement: <ErrorPage />
								}
							]
						: []),
					{
						path: routeNames.ADMIN_DEVICE_DETECTIONS,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_DEVICE_DETECTIONS} redirectPath={routeNames.DASHBOARD}>
								<AdminDeviceDetections />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_DEVICE_DETECTION_ADD,
						element: (
							<PrivateRoute requiredRoute={routeNames.ADMIN_DEVICE_DETECTION_ADD} redirectPath={routeNames.DASHBOARD}>
								<DeviceDetectionJob />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					},
					{
						path: routeNames.ADMIN_DEVICE_DETECTION_DETAILS,
						element: (
							<PrivateRoute
								requiredRoute={routeNames.ADMIN_DEVICE_DETECTION_DETAILS}
								redirectPath={routeNames.DASHBOARD}
							>
								<DeviceDetectionJob />
							</PrivateRoute>
						),
						errorElement: <ErrorPage />
					}
				]
			},
			{
				// LayoutManager can switch between LoggedNavigationLayout and AnonNavigationLayout depending on auth state.
				element: <LayoutManager />,
				children: [
					...(featureFlagGuideEnabled
						? [
								{
									path: routeNames.GUIDE_DETAILS,
									element: <GuideDetails />,
									errorElement: <ErrorPage />
								}
							]
						: []),
					...(featureFlagHandoversEnabled
						? [
								{
									path: routeNames.HANDOVER_DETAILS,
									element: <HandoverDetails />,
									errorElement: <ErrorPage />
								}
							]
						: [])
				]
			},
			{
				element: <NotFound />,
				path: routeNames.NOT_FOUND
			}
		]
	}
	///**** */
]);

function Root() {
	// The RouterProvider component is used to provide routing context to the components in your application. It ensures that routing-related functionality is accessible throughout your application's component hierarchy.
	// RouterProvider is particularly useful when using custom routers or when you need to access routing-related data or functions deep within your component tree.
	return <RouterProvider router={router} />;
}

export default Root;
